<div class="max-w-xl mx-auto bg-base-100 rounded-2xl overflow-hidden">
  <!-- Modal Header -->
  <div class="p-6 border-b border-base-200 bg-base-200/30">
    <div class="flex items-center justify-between">
      <div>
        <h2 class="text-2xl font-bold text-base-content">
          @if (isEditMode) { Edit Category } @else { Create New Category }
        </h2>
        <p class="text-sm opacity-60 mt-1">
          @if (isEditMode) { Modify the details of your category } @else { Add a new category to your catalog }
        </p>
      </div>
      <button class="btn btn-ghost btn-sm btn-circle" (click)="cancelAndReset()">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l18 18" />
        </svg>
      </button>
    </div>
  </div>

  <form [formGroup]="categoryForm" (ngSubmit)="submit()" class="p-6 space-y-6">
    <!-- Category Icon Upload -->
    <div class="form-control w-full">
      <label class="label px-1" for="iconInput">
        <span class="label-text font-bold text-base-content/70">Category Icon</span>
      </label>
      <app-image-uploader
        type="CATEGORY_IMAGE"
        [multiple]="false"
        [existingImageUrl]="iconUrl"
        (filesSelected)="onFilesValid($event)"
        (imageRemoved)="removeIcon()">
      </app-image-uploader>
    </div>

    <!-- Category Name -->
    <div class="form-control w-full">
      <label class="label px-1" for="name">
        <span class="label-text font-bold text-base-content/70">Category Name</span>
      </label>
      <input id="name" type="text" class="input input-bordered w-full bg-base-200/30 focus:bg-base-100 transition-all"
        placeholder="e.g. Electronics" formControlName="name" />
    </div>

    <div class="form-control w-full">
      <label class="label px-1" for="position">
        <span class="label-text font-bold text-base-content/70">Display Position</span>
      </label>
      <input id="position" type="number" min="0"
        class="input input-bordered w-full bg-base-200/30 focus:bg-base-100 transition-all"
        formControlName="position" />
      <div class="label px-1">
        <span class="label-text-alt opacity-50">Lower numbers appear first among categories at the same level.</span>
      </div>
    </div>

    <!-- Hierarchy Section -->
    <div class="bg-base-200/30 p-5 rounded-2xl space-y-4 border border-base-200">
      <div class="flex items-center justify-between mb-2">
        <div class="flex items-center gap-2">
          <div class="p-1.5 bg-primary/10 rounded-lg">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M7 7h.01M7 11h.01M7 15h.01M11 7h.01M11 11h.01M11 15h.01M15 7h.01M15 11h.01M15 15h.01" />
            </svg>
          </div>
          <span class="text-sm font-bold uppercase tracking-wider opacity-60">Hierarchy Settings</span>
        </div>
        @if (!isEditMode) {
        <div class="badge badge-outline badge-sm opacity-40 font-bold uppercase tracking-tighter">New Entry</div>
        }
      </div>

      @if (isEditMode) {
      <div class="form-control w-full">
        <label class="label" for="move-parent"><span class="label-text font-bold">Place under</span></label>
        <select id="move-parent" class="select select-bordered w-full" formControlName="moveParentId">
          <option value="">None (Top level)</option>
          @for (option of moveParentOptions; track option.id) {
            <option [value]="option.id" [disabled]="option.disabled">{{ option.label }}</option>
          }
        </select>
        <p class="text-xs text-base-content/60 mt-3" role="status">
          This also moves {{ movingSubcategoryCount }} nested categories. Products keep their existing category IDs and follow the new placement across stores.
          Review category-based offers and home sections after moving. Invalid destinations are disabled.
        </p>
      </div>
      } @else {
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Parent Category -->
        <div class="form-control w-full">
          <label class="label px-1" for="parentCategories">
            <span class="label-text text-xs font-bold opacity-60">Parent Category</span>
          </label>
          <div class="relative group">
            <select id="parentCategories"
              class="select select-bordered w-full bg-base-100 focus:border-primary/50 transition-all appearance-none pr-10"
              formControlName="parentCategoryId" (change)="onParentChange()">
              <option value="">None (Top level)</option>
              @for (cat of categories; track cat.id) {
              <option [value]="cat.id">{{ cat.name }}</option>
              }
            </select>
            <div class="absolute inset-y-0 right-3 flex items-center pointer-events-none opacity-40">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Sub Category -->
        <div class="form-control w-full">
          <label class="label px-1" for="subCategories">
            <span class="label-text text-xs font-bold opacity-60">Sub Category</span>
          </label>
          <div class="relative group" [class.opacity-50]="!childCategories.length">
            <select id="subCategories"
              class="select select-bordered w-full bg-base-100 focus:border-primary/50 transition-all appearance-none pr-10"
              formControlName="parentId" [disabled]="!childCategories.length">
              <option value="">Select sub category</option>
              @for (sub of childCategories; track sub.id) {
              <option [value]="sub.id">{{ sub.name }}</option>
              }
            </select>
            <div class="absolute inset-y-0 right-3 flex items-center pointer-events-none opacity-40">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2 mt-4 p-3 bg-base-100/50 rounded-lg border border-base-200">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary opacity-60" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span class="text-[11px] font-medium opacity-60">Organize your products by nesting categories up to 3 levels
          deep.</span>
      </div>
      }
    </div>

    <!-- Actions -->
    <div class="flex gap-3 pt-4 border-t border-base-200">
      <button type="button" class="btn btn-ghost flex-1" (click)="cancelAndReset()">
        Cancel
      </button>

      <button type="submit" class="btn btn-primary flex-1 shadow-md" [disabled]="loading || categoryForm.invalid || !hierarchyLoaded">
        @if (loading) {
        <span class="loading loading-spinner loading-sm"></span>
        }
        @if (isEditMode) { Update Category } @else { Create Category }
      </button>
    </div>
  </form>
</div>
